import {useState} from 'react';
import {
    MutationFunction,
    useMutation,
    UseMutationOptions,
} from '@tanstack/react-query';
import useMutationButtonProps from './mutation-button-state';
import {MutationRequest} from './types';

const useTrackedMutation = <
    TData = unknown,
    TError = unknown,
    TVariables = Record<string, unknown>,
    TContext = unknown,
>(
    mutationFunction: MutationFunction<TData, TVariables>,
    options?: Omit<
        UseMutationOptions<TData, TError, TVariables, TContext>,
        'mutationFn'
    >,
) => {
    const [mutationRequests, setMutationRequests] = useState<
        Record<string, MutationRequest>
    >({});

    const setMutationRequest = (id: string, state: MutationRequest | null) => {
        setMutationRequests((prev) => {
            const newState = {...prev};

            if (state) {
                newState[id] = state;
            } else {
                delete newState[id];
            }

            return newState;
        });
    };

    const buttonProps = useMutationButtonProps(mutationRequests);

    const {
        mutate: untrackedMutate,
        mutateAsync: untrackedMutateAsync,
        ...mutation
    } = useMutation<TData, TError, TVariables & {requestId: string}, TContext>(
        mutationFunction,
        options || {},
    );

    const mutate = (id: string, variables: TVariables) => {
        setMutationRequest(id, 'pending');

        return untrackedMutate({
            requestId: id,
            ...variables,
        });
    };

    const mutateAsync = (id: string, variables: TVariables) => {
        setMutationRequest(id, 'pending');

        return untrackedMutateAsync({
            requestId: id,
            ...variables,
        }).finally(() => {
            setMutationRequest(id, null);
        });
    };

    return {
        ...mutation,
        buttonProps,
        mutate,
        mutateAsync,
    };
};

export default useTrackedMutation;
